// // useSocket.ts
// import {ref, onUnmounted, Ref} from 'vue';
// import { io, Socket } from 'socket.io-client';



// export function useSocket(): SocketReturn {
//     // Initialize socket at the top level so it's accessible immediately
//     const socket = io(import.meta.env.VITE_BACKEND_BASE_URL || 'http://localhost:3000', {
//         withCredentials: true
//     });
//
//     const isConnected = ref(false);
//
//     socket.on('connect', () => {
//         isConnected.value = true;
//         console.log('Connected to socket', socket.id);
//     });
//
//     socket.on('connect_error', (error) => {
//         console.error('Socket connection error:', error);
//     });
//
//     onUnmounted(() => {
//         socket.disconnect();
//     });
//
//     return {
//         socket,
//         isConnected
//     };
// }

// useSocket.ts
import { Ref, ref, onBeforeUnmount, getCurrentInstance } from 'vue';
import { io, Socket } from 'socket.io-client';

interface SocketReturn {
    socket: Socket | null;
    isConnected: Ref<boolean>;
    joinRoom: (room: string) => void;
    leaveRoom: (room: string) => void;
}

// Single socket instance for the application
let socket: Socket | null = null;
const isConnected = ref(false);
const socketUsers = ref(0);

// Track all active rooms
const activeRooms = new Set<string>();

// Global cleanup function registered once
let cleanupRegistered = false;
const registerCleanup = () => {
    if (cleanupRegistered) return;

    // Handle page unload to properly disconnect
    window.addEventListener('beforeunload', () => {
        if (socket) {
            console.info('Page unloading, disconnecting socket');
            socket.disconnect();
            socket = null;
        }
    });

    cleanupRegistered = true;
};

export function useSocket(): SocketReturn {
    // Get component instance
    const instance = getCurrentInstance();

    // Only increment user count if in component context
    if (instance) {
        socketUsers.value++;
    }

    // Only create the socket if it doesn't exist
    if (!socket) {
        socket = io(import.meta.env.VITE_BACKEND_BASE_URL || 'http://localhost:3000', {
            withCredentials: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5,
            timeout: 20000,
            // Force new connection on reconnect to avoid stale session issues
            forceNew: false,
            // Handle transport-specific issues that can cause 400 errors
            transports: ['websocket', 'polling'],
            // Upgrade transport to avoid session ID conflicts
            upgrade: true
        });

        socket.on('connect', () => {
            isConnected.value = true;
            console.info('Connected to socket', socket?.id);

            // Rejoin any active rooms after reconnection
            activeRooms.forEach(room => {
                // Use the same logic as joinRoom function to emit the correct event
                if (room.startsWith('train:')) {
                    const trainId = room.replace('train:', '');
                    socket?.emit('joinTrain', trainId);
                } else if (room === 'lineup') {
                    socket?.emit('joinLineup');
                } else if (room.startsWith('request:')) {
                    const requestId = room.replace('request:', '');
                    socket?.emit('joinRequest', requestId);
                } else {
                    console.warn(`Reconnection: No specific handler for room type: ${room}`);
                }
            });
        });

        socket.on('disconnect', () => {
            isConnected.value = false;
            console.info('Disconnected from socket');
        });

        socket.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
            isConnected.value = false;
            
            // Handle specific error cases that can cause 400 errors
            if (error.message && error.message.includes('400')) {
                console.warn('Detected 400 error, likely invalid session ID. Forcing reconnection with new session.');
                // Disconnect and force a new connection to clear any stale session data
                socket?.disconnect();
                
                // Short delay before attempting to create new connection
                setTimeout(() => {
                    if (socket && !socket.connected) {
                        socket.connect();
                    }
                }, 1000);
            }
        });
        
        socket.on('error', (error) => {
            console.error('Socket error:', error);
            
            // Handle transport errors that commonly cause 400 status
            if (error && typeof error === 'object') {
                const errorMsg = JSON.stringify(error);
                if (errorMsg.includes('400') || errorMsg.includes('Bad Request')) {
                    console.warn('Transport error with 400 status detected, attempting recovery');
                    // Brief delay then try to reconnect
                    setTimeout(() => {
                        if (socket && !socket.connected) {
                            socket.connect();
                        }
                    }, 2000);
                }
            }
        });
        
        // Add a reconnection counter for better diagnostics
        let reconnectionAttempts = 0;
        socket.on('reconnect_attempt', (attempt) => {
            reconnectionAttempts = attempt;
            console.warn(`Socket reconnection attempt ${attempt}`);
        });
        
        socket.on('reconnect', () => {
            console.info(`Socket reconnected after ${reconnectionAttempts} attempts`);
            reconnectionAttempts = 0;
        });
        
        socket.on('reconnect_error', (error) => {
            console.error(`Socket reconnection error (attempt ${reconnectionAttempts}):`, error);
        });
        
        socket.on('reconnect_failed', () => {
            console.error('Socket reconnection failed after all attempts');
            
            // Implement exponential backoff strategy for persistent failures
            const implementManualReconnect = () => {
                let backoffDelay = 5000; // Start with 5 seconds
                const maxBackoff = 60000; // Max 1 minute
                let manualAttempts = 0;
                const maxManualAttempts = 10;
                
                const attemptManualReconnect = () => {
                    if (manualAttempts >= maxManualAttempts) {
                        console.error('Manual reconnection attempts exhausted. Socket connection permanently failed.');
                        return;
                    }
                    
                    manualAttempts++;
                    console.warn(`Manual reconnection attempt ${manualAttempts}/${maxManualAttempts} in ${backoffDelay}ms`);
                    
                    setTimeout(() => {
                        if (socket && !socket.connected) {
                            // Force a completely new socket instance to clear any persistent issues
                            socket.disconnect();
                            socket = null;
                            
                            // Create new socket with fresh configuration
                            socket = io(import.meta.env.VITE_BACKEND_BASE_URL || 'http://localhost:3000', {
                                withCredentials: true,
                                reconnection: true,
                                reconnectionAttempts: 3,
                                reconnectionDelay: 1000,
                                reconnectionDelayMax: 5000,
                                randomizationFactor: 0.5,
                                timeout: 20000,
                                forceNew: true, // Force new connection
                                transports: ['websocket', 'polling'],
                                upgrade: true
                            });
                            
                            socket.on('connect', () => {
                                console.info('Manual reconnection successful!');
                                isConnected.value = true;
                                // Reset manual reconnection state
                                manualAttempts = 0;
                                backoffDelay = 5000;
                                
                                // Rejoin active rooms
                                activeRooms.forEach(room => {
                                    if (room.startsWith('train:')) {
                                        const trainId = room.replace('train:', '');
                                        socket?.emit('joinTrain', trainId);
                                    } else if (room === 'lineup') {
                                        socket?.emit('joinLineup');
                                    } else if (room.startsWith('request:')) {
                                        const requestId = room.replace('request:', '');
                                        socket?.emit('joinRequest', requestId);
                                    }
                                });
                            });
                            
                            socket.on('connect_error', () => {
                                console.error(`Manual reconnection attempt ${manualAttempts} failed`);
                                // Exponential backoff
                                backoffDelay = Math.min(backoffDelay * 2, maxBackoff);
                                attemptManualReconnect();
                            });
                        }
                    }, backoffDelay);
                };
                
                attemptManualReconnect();
            };
            
            // Start manual reconnection after a brief delay
            setTimeout(implementManualReconnect, 2000);
        });

        // Register global cleanup
        registerCleanup();
    }

    // Helper functions for joining/leaving rooms that match backend handlers
    const joinRoom = (room: string) => {
        if (!socket || !room) return;
        
        // Parse the room string to determine the correct event
        if (room.startsWith('train:')) {
            const trainId = room.replace('train:', '');
            socket.emit('joinTrain', trainId);
        } else if (room === 'lineup') {
            socket.emit('joinLineup');
        } else if (room.startsWith('request:')) {
            const requestId = room.replace('request:', '');
            socket.emit('joinRequest', requestId);
        } else {
            console.warn(`No specific handler for room type: ${room}`);
            return; // Don't add to activeRooms if we can't properly join
        }
        
        activeRooms.add(room);
    };

    const leaveRoom = (room: string) => {
        if (!socket || !room) return;
        
        // Parse the room string to determine the correct event
        if (room.startsWith('train:')) {
            const trainId = room.replace('train:', '');
            socket.emit('leaveTrain', trainId);
        } else if (room === 'lineup') {
            socket.emit('leaveLineup');
        } else if (room.startsWith('request:')) {
            const requestId = room.replace('request:', '');
            socket.emit('leaveRequest', requestId);
        } else {
            console.warn(`No specific handler for leaving room type: ${room}`);
        }
        
        activeRooms.delete(room);
    };

    // Clean up when component unmounts - only register if in component context
    if (instance) {
        onBeforeUnmount(() => {
            socketUsers.value--;

            // If this was the last component using the socket, disconnect
            if (socketUsers.value <= 0 && socket) {
                console.info('No more components using socket, disconnecting');
                socket.disconnect();
                socket = null;
                socketUsers.value = 0;
                activeRooms.clear();
            }
        });
    } else {
        // If not in component context, we still need to track usage
        // but we'll rely on the global cleanup for disconnection
        console.info('useSocket called outside component context');
    }

    return {
        socket,
        isConnected,
        joinRoom,
        leaveRoom
    };
}
